"use client";
import React, { useRef, useEffect, useMemo } from "react";
import throttle from "lodash.throttle";
import debounce from "lodash.debounce";

export function useThrottle<T>(value: T, interval = 500) {
  const [throttledValue, setThrottledValue] = React.useState(value);
  const lastUpdated = React.useRef<number | null>(null);

  React.useEffect(() => {
    const now = Date.now();

    if (lastUpdated.current && now >= lastUpdated.current + interval) {
      lastUpdated.current = now;
      setThrottledValue(value);
    } else {
      const id = window.setTimeout(() => {
        lastUpdated.current = now;
        setThrottledValue(value);
      }, interval);

      return () => window.clearTimeout(id);
    }
  }, [value, interval]);

  return throttledValue;
}

/** Configuration options for controlling the behavior of the debounced function. */
type DebounceOptions = {
  /**
   * Determines whether the function should be invoked on the leading edge of the timeout.
   * @default false
   */
  leading?: boolean;
  /**
   * Determines whether the function should be invoked on the trailing edge of the timeout.
   * @default false
   */
  trailing?: boolean;
  /**
   * The maximum time the specified function is allowed to be delayed before it is invoked.
   */
  maxWait?: number;
};

/** Configuration options for controlling the behavior of the throttled function. */
type ThrottleOptions = {
  /**
   * Determines whether the function should be invoked on the leading edge of the timeout.
   * @default true
   */
  leading?: boolean;
  /**
   * Determines whether the function should be invoked on the trailing edge of the timeout.
   * @default true
   */
  trailing?: boolean;
};

/** Functions to manage a debounced callback. */
type ControlFunctions = {
  /** Cancels pending function invocations. */
  cancel: () => void;
  /** Immediately invokes pending function invocations. */
  flush: () => void;
  /**
   * Checks if there are any pending function invocations.
   * @returns `true` if there are pending invocations, otherwise `false`.
   */
  isPending: () => boolean;
};

/**
 * Represents the state and control functions of a debounced callback.
 * Subsequent calls to the debounced function return the result of the last invocation.
 * Note: If there are no previous invocations, the result will be undefined.
 * Ensure proper handling in your code.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DebouncedState<T extends (...args: any) => ReturnType<T>> = ((
  ...args: Parameters<T>
) => ReturnType<T> | undefined) &
  ControlFunctions;

/**
 * Represents the state and control functions of a throttled callback.
 * Subsequent calls to the throttled function return the result of the last invocation.
 * Note: If there are no previous invocations, the result will be undefined.
 * Ensure proper handling in your code.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ThrottledState<T extends (...args: any) => ReturnType<T>> = ((
  ...args: Parameters<T>
) => ReturnType<T> | undefined) &
  ControlFunctions;

/**
 * Custom hook that creates a debounced version of a callback function.
 * @template T - Type of the original callback function.
 * @param {T} func - The callback function to be debounced.
 * @param {number} delay - The delay in milliseconds before the callback is invoked (default is `500` milliseconds).
 * @param {DebounceOptions} [options] - Options to control the behavior of the debounced function.
 * @returns {DebouncedState<T>} A debounced version of the original callback along with control functions.
 * @public
 * @see [Documentation](https://usehooks-ts.com/react-hook/use-debounce-callback)
 * @example
 * ```tsx
 * const debouncedCallback = useDebounceCallback(
 *   (searchTerm) => {
 *     // Perform search after user stops typing for 500 milliseconds
 *     searchApi(searchTerm);
 *   },
 *   500
 * );
 *
 * // Later in the component
 * debouncedCallback('react hooks'); // Will invoke the callback after 500 milliseconds of inactivity.
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDebounceCallback<T extends (...args: any) => ReturnType<T>>(
  func: T,
  delay = 500,
  options?: DebounceOptions,
): DebouncedState<T> {
  const debouncedFunc = useRef<ReturnType<typeof debounce> | undefined>(
    undefined,
  );

  useUnmount(() => {
    if (debouncedFunc.current) {
      debouncedFunc.current.cancel();
    }
  });

  const debounced = useMemo(() => {
    const debouncedFuncInstance = debounce(func, delay, options);

    const wrappedFunc: DebouncedState<T> = (...args: Parameters<T>) => {
      return debouncedFuncInstance(...args);
    };

    wrappedFunc.cancel = () => {
      debouncedFuncInstance.cancel();
    };

    wrappedFunc.isPending = () => {
      return !!debouncedFunc.current;
    };

    wrappedFunc.flush = () => {
      return debouncedFuncInstance.flush();
    };

    return wrappedFunc;
  }, [func, delay, options]);

  // Update the debounced function ref whenever func, wait, or options change
  useEffect(() => {
    debouncedFunc.current = debounce(func, delay, options);
  }, [func, delay, options]);

  return debounced;
}

/**
 * Custom hook that creates a throttled version of a callback function.
 * @template T - Type of the original callback function.
 * @param {T} func - The callback function to be throttled.
 * @param {number} interval - The interval in milliseconds between invocations (default is `500` milliseconds).
 * @param {ThrottleOptions} [options] - Options to control the behavior of the throttled function.
 * @returns {ThrottledState<T>} A throttled version of the original callback along with control functions.
 * @public
 * @example
 * ```tsx
 * const throttledCallback = useThrottleCallback(
 *   (value) => {
 *     // Handle scroll event at most once every 500 milliseconds
 *     handleScroll(value);
 *   },
 *   500
 * );
 *
 * // Later in the component
 * throttledCallback(scrollPosition); // Will invoke the callback at most once per 500 milliseconds.
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useThrottleCallback<T extends (...args: any) => ReturnType<T>>(
  func: T,
  interval = 500,
  options?: ThrottleOptions,
): ThrottledState<T> {
  const throttledFunc = useRef<ReturnType<typeof throttle> | undefined>(
    undefined,
  );

  useUnmount(() => {
    if (throttledFunc.current) {
      throttledFunc.current.cancel();
    }
  });

  const throttled = useMemo(() => {
    const throttledFuncInstance = throttle(func, interval, options);

    const wrappedFunc: ThrottledState<T> = (...args: Parameters<T>) => {
      return throttledFuncInstance(...args);
    };

    wrappedFunc.cancel = () => {
      throttledFuncInstance.cancel();
    };

    wrappedFunc.isPending = () => {
      return !!throttledFunc.current;
    };

    wrappedFunc.flush = () => {
      return throttledFuncInstance.flush();
    };

    return wrappedFunc;
  }, [func, interval, options]);

  // Update the throttled function ref whenever func, interval, or options change
  useEffect(() => {
    throttledFunc.current = throttle(func, interval, options);
  }, [func, interval, options]);

  return throttled;
}

/**
 * Custom hook that runs a cleanup function when the component is unmounted.
 * @param {() => void} func - The cleanup function to be executed on unmount.
 * @public
 * @see [Documentation](https://usehooks-ts.com/react-hook/use-unmount)
 * @example
 * ```tsx
 * useUnmount(() => {
 *   // Cleanup logic here
 * });
 * ```
 */
export function useUnmount(func: () => void) {
  const funcRef = useRef(func);

  funcRef.current = func;

  useEffect(
    () => () => {
      funcRef.current();
    },
    [],
  );
}
